import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, setToken } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

// Blocking modal shown after a superadmin password reset: the partner MUST
// choose their own password before using the dashboard.
export default function ForcePasswordChange() {
  const { t } = useLang();
  const { partner, setPartner } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (!partner?.must_change_password) return null;

  async function submit() {
    if (pw.length < 8) return toast.error(t("hc.8CharactersMinimum"));
    if (pw !== confirm) return toast.error(t("hc.passwordsDoNotMatch"));
    setSaving(true);
    try {
      const r = await api("/auth/me/change-password", { method: "POST", body: { new_password: pw } });
      if (r.access_token) setToken(r.access_token);
      setPartner(r.partner);
      toast.success(t("hc.passwordUpdated"));
    } catch (e) {
      toast.error(e.message || t("hc.updateFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="w-[95vw] sm:max-w-md rounded-2xl" data-testid="force-password-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-findo-danger" />
            <DialogTitle>{t("hc.chooseANewPassword")}</DialogTitle>
          </div>
          <DialogDescription>
            {t("hc.pwdResetByTeam")}{" "}
            {t("hc.pleaseSetYourOwnPassword")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="fp-new" className="text-sm">{t("hc.newPassword")}</Label>
            <Input id="fp-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              className="mt-1.5 rounded-xl" data-testid="force-password-new" />
          </div>
          <div>
            <Label htmlFor="fp-confirm" className="text-sm">{t("hc.confirmThePassword")}</Label>
            <Input id="fp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 rounded-xl" data-testid="force-password-confirm" />
          </div>
          <Button onClick={submit} disabled={saving} className="w-full rounded-xl font-semibold" data-testid="force-password-submit">
            {saving ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("hc.saving")}</span> : t("hc.saveAndContinue")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
